// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/**
 * Single source of truth for fuel classes, their display labels and their
 * colours. The SAME colour represents the same fuel in every view — the donut,
 * the stacked area, the map legend, the matrix, the mini-bars and the drawer —
 * so a reader learns the colour once. Never hard-code a fuel colour elsewhere.
 */
export type Fuel = 'petrol' | 'diesel' | 'hybrid' | 'bev' | 'other';

/** Draw order: fossil first, then electrified, then the residual. */
export const FUEL_ORDER: Fuel[] = ['petrol', 'diesel', 'hybrid', 'bev', 'other'];

export const FUEL_LABEL: Record<Fuel, string> = {
  petrol: 'Petrol',
  diesel: 'Diesel',
  hybrid: 'Hybrid',
  bev: 'Electric',
  other: 'Other',
};

export const FUEL_COLOUR: Record<Fuel, string> = {
  petrol: '#64748b', // slate — the fossil default
  diesel: '#a16207', // sooty dark-amber
  hybrid: '#0ea5e9', // sky — part-electric
  bev: '#16a34a', // green — battery-electric (the site accent)
  other: '#cbd5e1', // pale grey — dual fuel / LPG / not stated
};

/** Longer descriptions for legends and the About panel. */
export const FUEL_DESC: Record<Fuel, string> = {
  petrol: 'Petrol / unleaded internal-combustion engines',
  diesel: 'Diesel internal-combustion engines',
  hybrid: 'Petrol-electric hybrids (self-charging and plug-in), not counted as fully electric',
  bev: 'Battery-electric and hydrogen fuel-cell vehicles — plug-in, zero tailpipe',
  other: 'Dual fuel (LPG), other and not-stated motive power',
};

export interface Mix {
  petrol: number;
  diesel: number;
  hybrid: number;
  bev: number;
  other: number;
  total: number;
}

export function fuelShare(mix: Mix, fuel: Fuel): number | null {
  return mix.total > 0 ? (mix[fuel] / mix.total) * 100 : null;
}

/** Electrified = battery-electric + hybrid, as a share of the total. */
export function evShare(mix: Mix): number | null {
  return mix.total > 0 ? ((mix.bev + mix.hybrid) / mix.total) * 100 : null;
}
